import { z } from 'zod'
import { trainerErrorEnvelopeSchema } from './trainer-event-read'
import {
  TRAINER_CONTRACT_VERSION,
  trainerEventSummarySchema,
  trainerWriteSideEffectsSchema,
} from './trainer-event-read'
import { trainerTrainingCentreNoteSummarySchema, trainerTrainingCentreSummarySchema } from './trainer-training-centre-read'

const optionalLegacyValue = z.unknown().optional()
const providerModeSchema = z.enum(['dry_run', 'live'])
const notificationActionSchema = z.enum([
  'training_centre.assignment.notify',
  'training_centre.status.notify',
  'training_centre.note.notify',
  'training_centre.conversion.notify',
])

/**
 * Transport request contracts intentionally preserve the handwritten handler
 * validators. They describe the existing legacy wire aliases without moving
 * validation, coercion, or error-message ownership out of those handlers.
 */
export const trainerTrainingCentreCreateRequestSchema = z.object({
  name: optionalLegacyValue,
  venue_name: z.string(),
  location: z.string(),
  event_name: z.string(),
  requested_start_date: z.string(),
  requested_end_date: optionalLegacyValue,
  event_desc: optionalLegacyValue,
  additional_ins: optionalLegacyValue,
  no_participants: optionalLegacyValue,
  course_type: z.string(),
  budgets: optionalLegacyValue,
  company_name: optionalLegacyValue,
  contact_person_name: z.string(),
  contact_person_mobile_no: z.string(),
  contact_person_email: z.string(),
  requirements: optionalLegacyValue,
}).passthrough()

export const trainerTrainingCentreUpdateRequestSchema = z.object({
  active: z.boolean(),
}).passthrough()

export const trainerTrainingCentreAssignmentRequestSchema = z.object({
  assigned_to_user_id: optionalLegacyValue,
  assignedToUserId: optionalLegacyValue,
}).passthrough()

export const trainerTrainingCentreStatusRequestSchema = z.object({
  request_status: z.string(),
}).passthrough()

export const trainerTrainingCentreNoteCreateRequestSchema = z.object({
  note_text: optionalLegacyValue,
  noteText: optionalLegacyValue,
  note_type: optionalLegacyValue,
  noteType: optionalLegacyValue,
}).passthrough()

export const trainerTrainingCentreConversionRequestSchema = z.object({}).passthrough()

const trainerTrainingCentreExternalNotificationOutboxSchema = z.object({
  enqueued: z.boolean(),
  itemCount: z.number().int().nonnegative(),
  providerMode: providerModeSchema,
  provider: z.literal('external_notification'),
  actionType: z.literal('training_centre.external.notify'),
  providerConfigured: z.boolean(),
  consentChecked: z.boolean(),
}).passthrough()

const trainerTrainingCentreNotificationReadinessSchema = z.object({
  inApp: z.object({
    status: z.enum(['queued', 'gated']),
    provider: z.literal('trainer_notification'),
    providerMode: providerModeSchema,
    itemCount: z.number().int().nonnegative(),
    actionType: notificationActionSchema,
  }).passthrough(),
  external: z.object({
    status: z.enum(['dry_run', 'ready', 'blocked']),
    provider: z.literal('external_notification'),
    providerMode: providerModeSchema,
    itemCount: z.number().int().nonnegative(),
    actionType: z.literal('training_centre.external.notify'),
    providerConfigured: z.boolean(),
    consentChecked: z.boolean(),
    deliveryChannels: z.array(z.string()),
    liveDeliveryBlocked: z.boolean(),
    blockerReason: z.string().nullable(),
  }).passthrough(),
  rawPayloadReturned: z.literal(false),
  secretReturned: z.literal(false),
}).passthrough()

export const trainerTrainingCentreNotificationOutboxSchema = z.object({
  enqueued: z.boolean(),
  itemCount: z.number().int().nonnegative(),
  providerMode: providerModeSchema,
  provider: z.literal('trainer_notification'),
  actionType: notificationActionSchema,
  externalNotificationOutbox: trainerTrainingCentreExternalNotificationOutboxSchema,
  notificationReadiness: trainerTrainingCentreNotificationReadinessSchema,
}).passthrough()

const trainerTrainingCentreMutationResponseShape = {
  contract_version: z.literal(TRAINER_CONTRACT_VERSION),
  trainingCentre: trainerTrainingCentreSummarySchema,
  idempotentReplay: z.boolean(),
  sideEffects: trainerWriteSideEffectsSchema.passthrough(),
  notificationOutbox: trainerTrainingCentreNotificationOutboxSchema.optional(),
}

export const trainerTrainingCentreCreateResponseSchema = z.object(trainerTrainingCentreMutationResponseShape).passthrough()
export const trainerTrainingCentreUpdateResponseSchema = z.object(trainerTrainingCentreMutationResponseShape).passthrough()
export const trainerTrainingCentreAssignmentResponseSchema = z.object(trainerTrainingCentreMutationResponseShape).passthrough()
export const trainerTrainingCentreStatusResponseSchema = z.object(trainerTrainingCentreMutationResponseShape).passthrough()

export const trainerTrainingCentreNoteCreateResponseSchema = z.object({
  contract_version: z.literal(TRAINER_CONTRACT_VERSION),
  note: trainerTrainingCentreNoteSummarySchema,
  idempotentReplay: z.boolean(),
  sideEffects: trainerWriteSideEffectsSchema.passthrough(),
  notificationOutbox: trainerTrainingCentreNotificationOutboxSchema.optional(),
}).passthrough()

export const trainerTrainingCentreConversionResponseSchema = z.object({
  contract_version: z.literal(TRAINER_CONTRACT_VERSION),
  trainingCentre: trainerTrainingCentreSummarySchema,
  event: trainerEventSummarySchema,
  idempotentReplay: z.boolean(),
  sideEffects: trainerWriteSideEffectsSchema.passthrough(),
  notificationOutbox: trainerTrainingCentreNotificationOutboxSchema.optional(),
}).passthrough()

export const trainerTrainingCentreWriteErrorEnvelopeSchema = trainerErrorEnvelopeSchema

export type TrainerTrainingCentreCreateRequest = z.infer<typeof trainerTrainingCentreCreateRequestSchema>
export type TrainerTrainingCentreUpdateRequest = z.infer<typeof trainerTrainingCentreUpdateRequestSchema>
export type TrainerTrainingCentreAssignmentRequest = z.infer<typeof trainerTrainingCentreAssignmentRequestSchema>
export type TrainerTrainingCentreStatusRequest = z.infer<typeof trainerTrainingCentreStatusRequestSchema>
export type TrainerTrainingCentreNoteCreateRequest = z.infer<typeof trainerTrainingCentreNoteCreateRequestSchema>
export type TrainerTrainingCentreConversionRequest = z.infer<typeof trainerTrainingCentreConversionRequestSchema>
export type TrainerTrainingCentreNotificationOutbox = z.infer<typeof trainerTrainingCentreNotificationOutboxSchema>
export type TrainerTrainingCentreCreateResponse = z.infer<typeof trainerTrainingCentreCreateResponseSchema>
export type TrainerTrainingCentreUpdateResponse = z.infer<typeof trainerTrainingCentreUpdateResponseSchema>
export type TrainerTrainingCentreAssignmentResponse = z.infer<typeof trainerTrainingCentreAssignmentResponseSchema>
export type TrainerTrainingCentreStatusResponse = z.infer<typeof trainerTrainingCentreStatusResponseSchema>
export type TrainerTrainingCentreNoteCreateResponse = z.infer<typeof trainerTrainingCentreNoteCreateResponseSchema>
export type TrainerTrainingCentreConversionResponse = z.infer<typeof trainerTrainingCentreConversionResponseSchema>
